import { formatTimezone } from '~shared/timeutils'
import PropTypes from 'prop-types'
import eventBus from '../../../utils/eventBus'
import { useAtomValue, useAtom } from 'jotai'
import { rundownAtom, autoScrollAtom } from '../../../store/rundown.store'
import AdvancedSelect from '../../interactives/AdvancedSelect'
import { useMemo, useLayoutEffect, lazy } from 'react'
import { formatDate } from 'date-fns'
import { tz } from '@date-fns/tz'
import { useModal } from '../../../features/modal-manager/index.ts'

const RundownDateModal = lazy(() => import('../../modal/RundownDateModal'))

export default function RundownDayTZ({ timezone, readonly = false }) {
  const rundown = useAtomValue(rundownAtom)
  const [autoScroll, setAutoScroll] = useAtom(autoScrollAtom)

  const isAutoScrollEnabled = autoScroll[rundown.id] !== false

  const [dateModal, renderDateModal] = useModal(RundownDateModal)

  const rundownId = useMemo(() => rundown.id, [rundown.id])

  function getFormattedTimezone() {
    if (!timezone) return 'Choose a timezone...'
    if (timezone === 'UTC') return formatTimezone(timezone, 'abbr')
    return formatTimezone(timezone, 'long')
  }

  useLayoutEffect(() => {
    if (!rundownId || !autoScroll) return

    const savedAutoscrollSetting = autoScroll[rundownId]

    if (typeof savedAutoscrollSetting === 'undefined') {
      setAutoScroll(rundownId, true)
    }

    // Never use state setters in the dependency array, as it will cause a max update depth error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rundownId])

  return (
    <div className="flex space-x-4">
      <section>
        <div className="px-2 text-gray-500 font-semibold whitespace-pre text-[10px]">
          Rundown date
        </div>
        <button
          className="transition-colors px-2 h-6 text-gray-300 rounded-sm enabled:hover:bg-white/20 overflow-hidden line-clamp-1 text-left text-sm"
          onClick={dateModal.show}
          disabled={readonly}
        >
          {formatDate(rundown.startTime, 'PP', { in: tz(timezone) })}
        </button>
      </section>
      <section>
        <div className="px-2 text-gray-500 font-semibold whitespace-pre text-[10px]">
          Timezone
        </div>
        <span className="text-sm">
          <button
            className="transition-colors px-2 h-6 text-gray-300 rounded-xs enabled:hover:bg-white/20 overflow-hidden line-clamp-1 text-left"
            onClick={() => eventBus.$emit('openTimezoneModal')}
            title={
              timezone
                ? `${formatTimezone(timezone, 'long')}, ${formatTimezone(timezone, 'city')}, ${formatTimezone(timezone, 'offset')}`
                : undefined
            }
            disabled={readonly}
          >
            {getFormattedTimezone()}
          </button>
        </span>
      </section>
      <section>
        <div className="px-2 text-gray-500 font-semibold whitespace-pre text-[10px]">
          Auto-Scroll
        </div>
        <span className="text-sm">
          <AdvancedSelect
            className="transition-colors px-2! h-6! text-gray-300 rounded-xs bg-transparent hover:bg-white/20 overflow-hidden w-48! focus:outline-hidden focus:ring-3"
            options={[
              {
                value: 'true',
                label: 'Activated',
                detail: 'Automatically scrolls to the current cue',
              },
              {
                value: 'false',
                label: 'Deactivated',
                detail: 'Scroll the rundown at your own pace',
              },
            ]}
            value={String(isAutoScrollEnabled)}
            setValue={(value) => setAutoScroll(rundown.id, value === 'true')}
          />
        </span>
      </section>

      {renderDateModal()}
    </div>
  )
}

RundownDayTZ.propTypes = {
  timezone: PropTypes.string,
  readonly: PropTypes.bool,
}
